import Modal from "@ch/components/Modals/Modal";
import delay from "@ch/helpers/utils/delay";
import { type FC, useEffect } from "react";

export const KEM_LOCATION_PERMISSION_MODAL_ID = "kem-location-permission-modal";

export type KemLocationPermissionModalVariant = "prompt" | "denied";

export interface KemLocationPermissionModalProps {
  isOpen: boolean;
  variant?: KemLocationPermissionModalVariant;
  onClose: () => void;
  onConfirm: () => void;
}

const closeDialogue = () => {
  const dialogue = document.getElementById(
    KEM_LOCATION_PERMISSION_MODAL_ID,
  ) as HTMLDialogElement | null;

  dialogue?.close();
};

const KemLocationPermissionModal: FC<KemLocationPermissionModalProps> = ({
  isOpen,
  variant = "prompt",
  onClose,
  onConfirm,
}) => {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      await delay(100);
      if (cancelled) {
        return;
      }

      const dialogue = document.getElementById(
        KEM_LOCATION_PERMISSION_MODAL_ID,
      ) as HTMLDialogElement | null;

      if (!dialogue || dialogue.open) {
        return;
      }

      dialogue.showModal();
    })();

    return () => {
      cancelled = true;
      closeDialogue();
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleClose = () => {
    closeDialogue();
    onClose();
  };

  const handleConfirm = () => {
    closeDialogue();
    onConfirm();
  };

  const isDenied = variant === "denied";

  return (
    <Modal
      cancelButtonLabel={isDenied ? undefined : "Not now"}
      confirmButtonLabel={isDenied ? "OK" : "Continue"}
      handleCancel={isDenied ? undefined : handleClose}
      handleConfirm={handleConfirm}
      headerTitle="Enable location access"
      isModalOpen
      modalBoxClassName="md:w-[480px] !h-fit"
      modalId={KEM_LOCATION_PERMISSION_MODAL_ID}
      onDismiss={handleClose}
    >
      <div className="space-y-4 text-sm text-gray-700">
        {isDenied ? (
          <>
            <p>
              Location access is currently blocked in your browser settings, so
              Kem cannot request it again from this page.
            </p>
            <p>
              To enable location, open your browser settings, allow location for
              this site, then refresh the page.
            </p>
          </>
        ) : (
          <>
            <p>
              Kem uses your location to personalize health guidance. Your
              browser will ask for permission next.
            </p>
            <p>
              Please choose <strong>Allow</strong> when the location popup
              appears. If you deny it, your browser will not show that prompt
              again and you would need to re-enable location in your browser
              settings.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

export default KemLocationPermissionModal;
